import { useEffect, useState } from 'react';
import { currencyHint } from '../utils/currency';

/**
 * Loads the credit-pack catalogue from the backend so prices live in exactly
 * one place (backend/src/config/pricing.js) instead of being duplicated across
 * the landing page and the upgrade modal.
 *
 * The currency the buyer is charged in is decided server-side; the hint we send
 * is advisory only (see utils/currency).
 */

// Used ONLY when the request itself fails, so the pricing section never
// collapses on a flaky network. It is deliberately NOT used when the server
// answers with an empty catalogue — that means no Dodo products are configured,
// and rendering these packs would offer cards that cannot be bought.
// It is also not the initial state: painting these prices first would flash USD
// at a buyer about to be shown INR. Mirrors the USD side of the backend.
const FALLBACK = {
  currency: 'USD',
  packs: [
    {
      id: 'starter', name: 'Starter', tagline: 'For shorts and reels', icon: 'zap',
      popular: false, minutes: 120, maxVideoMinutes: 5, amount: 12, price: '$12', perMinute: '$0.10', period: 'one-time',
      features: ['2 hours of video', 'Max video length 5 min', 'Minutes never expire', 'Subtitle burn-in', 'SRT / VTT / ASS export'],
    },
    {
      id: 'growth', name: 'Growth', tagline: 'For serious creators', icon: 'trending-up',
      popular: true, minutes: 360, maxVideoMinutes: 5, amount: 29, price: '$29', perMinute: '$0.08', period: 'one-time',
      features: ['6 hours of video', 'Max video length 5 min', 'Minutes never expire', 'Subtitle burn-in + greenscreen', 'Word-level timestamps'],
    },
    {
      id: 'agency', name: 'Agency', tagline: 'For teams, bulk creators and long videos', icon: 'crown',
      popular: false, minutes: 720, maxVideoMinutes: 15, amount: 49, price: '$49', perMinute: '$0.07', period: 'one-time',
      features: ['12 hours of video', 'Max video length 15 min', 'Minutes never expire', 'Everything in Growth', 'Priority support'],
    },
  ],
};

export const usePricing = () => {
  const [catalog, setCatalog] = useState({ currency: 'USD', packs: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const base = import.meta.env.VITE_API_URL || 'http://localhost:5000';
    const hint = currencyHint();
    const url = `${base}/api/payments/pricing${hint ? `?currency=${hint}` : ''}`;

    fetch(url, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('pricing request failed'))))
      .then((data) => {
        // An empty packs array is a valid answer ("nothing is configured to
        // sell"), so it is honoured rather than papered over with FALLBACK.
        setCatalog(Array.isArray(data?.packs) ? data : FALLBACK);
        setLoading(false);
      })
      .catch((err) => {
        // An aborted request means the component unmounted — leave state alone.
        if (err?.name === 'AbortError') return;
        setCatalog(FALLBACK);
        setLoading(false);
      });

    return () => controller.abort();
  }, []);

  return { ...catalog, loading };
};

export default usePricing;
